import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * useRecentServiceJobs — last few completed Service Fusion jobs for the
 * caller's customer that don't have a QBO invoice. Sky's convention:
 * deliveries get invoiced; service visits don't. So a recent SF job with
 * no matching invoice IS a service visit.
 *
 * Surfaced on /home (and inside the service-tickets card) so customers
 * see "your last few Brix service visits" before they file a new ticket.
 * Helps prevent duplicate requests.
 */

export interface RecentServiceJob {
  jobId: string;
  status: string | null;
  description: string | null;
  category: string | null;
  completedAt: string | null;
  technicianName: string | null;
  location: string | null;
}

interface ApiResponse {
  ok: boolean;
  jobs?: Array<{
    job_id: string;
    status: string | null;
    description: string | null;
    category: string | null;
    completed_at: string | null;
    technician_name: string | null;
    location: string | null;
  }>;
  note?: string;
  warning?: string;
}

export type ServiceJobStatusFilter = 'completed' | 'open' | 'all';

export function useRecentServiceJobs(
  daysLookback: number = 90,
  statusFilter: ServiceJobStatusFilter = 'completed',
) {
  return useQuery<RecentServiceJob[]>({
    queryKey: ['recent-service-jobs', daysLookback, statusFilter],
    queryFn: async () => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) return [];

      const res = await fetch(
        `/.netlify/functions/get-recent-service-jobs?days=${daysLookback}&status=${statusFilter}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return [];
      const body = (await res.json()) as ApiResponse;
      if (!body.ok || !body.jobs) return [];
      return body.jobs.map(
        (j): RecentServiceJob => ({
          jobId: j.job_id,
          status: j.status,
          description: j.description,
          category: j.category,
          completedAt: j.completed_at,
          technicianName: j.technician_name,
          location: j.location,
        }),
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}
